// src/pages/BlogListPage.jsx
import React, { useState, useEffect } from "react";
import BlogApi from "../../api/BlogApi";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import headerImg from "../../images/service/yuukke-logo.png";
import { API_URL } from "../../config/app-config";

const BRAND = {
  red:       "#7a0024",
  redHover:  "#9b0030",
  redLight:  "#ffeef3",
  redBorder: "#f0dde3",
  dark:      "#1a0a0f",
  body:      "#6b4550",
  muted:     "#b8929e",
  bg:        "#fff9f7",
  surface:   "#fff",
  border:    "#f0dde3",
  borderMid: "#e8d5db",
};

const TAGS = ["All", "Growth", "Tips", "Feature", "Story", "Guide", "News"];

const TAG_STYLE = {
  Growth:  { color:"#15803d", bg:"#f0fdf4",  border:"#bbf7d0" },
  Tips:    { color:"#7a0024", bg:"#ffeef3",  border:"#f0dde3" },
  Feature: { color:"#5b21b6", bg:"#f5f3ff",  border:"#ddd6fe" },
  Story:   { color:"#854f0b", bg:"#fffbeb",  border:"#fde68a" },
  Guide:   { color:"#1e40af", bg:"#eff6ff",  border:"#bfdbfe" },
  News:    { color:"#065f46", bg:"#ecfdf5",  border:"#a7f3d0" },
};

// ── Shared thumbnail renderer ──────────────────────────────────────────────
// ── Matches your exact endpoint: GET /auth/image/{id} ─────────────────
const IMAGE_BASE_URL = API_URL+ "auth/image/";

function resolveThumbUrl(thumbnailUrl) {
  if (!thumbnailUrl || !thumbnailUrl.trim()) return null;

  // Already a full URL (Unsplash / AI suggested) — use as-is
  if (thumbnailUrl.startsWith("http://") || thumbnailUrl.startsWith("https://")) {
    return thumbnailUrl;
  }

  // Raw UUID from DB → GET /auth/image/{uuid}
  // e.g. "46aa2baa-9c12-4233-90a0-d3d7dbba68a3"
  // → "https://bml.betalearnings.com/bca/auth/image/46aa2baa-9c12-4233-90a0-d3d7dbba68a3"
  return IMAGE_BASE_URL + thumbnailUrl.trim();
}

function BlogThumb({ blog, style = {} }) {
  const [err, setErr] = useState(false);
  const url = resolveThumbUrl(blog.thumbnailUrl);

  if (url && !err) {
    return (
      <img
        src={url}
        alt={blog.title}
        onError={() => setErr(true)}
        style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", ...style }}
      />
    );
  }
  return (
    <span style={{ fontSize: style.fontSize || "4rem" }}>
      {blog.emoji || "📝"}
    </span>
  );
}

// ─── Skeleton ──────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:"1.5rem" }}>
      {[1,2,3,4,5,6].map(i => (
        <div key={i} style={{ background:BRAND.surface, borderRadius:16,
          overflow:"hidden", border:`1px solid ${BRAND.border}` }}>
          <div style={{ height:190, background:BRAND.redLight, animation:"pulse 1.5s ease infinite" }} />
          <div style={{ padding:"1.5rem" }}>
            {[35,90,75,50].map((w,j) => (
              <div key={j} style={{ height:j===1?17:11, background:BRAND.border, borderRadius:6,
                marginBottom:j===1?12:8, width:w+"%" }} />
            ))}
          </div>
        </div>
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:.6}50%{opacity:.3}}`}</style>
    </div>
  );
}

// ─── Tag Pill ──────────────────────────────────────────────────────────────
function TagPill({ tag }) {
  const s = TAG_STYLE[tag] || { color:BRAND.body, bg:BRAND.bg, border:BRAND.border };
  return (
    <span style={{ display:"inline-block", fontSize:"11px", fontWeight:700,
      letterSpacing:".08em", textTransform:"uppercase",
      color:s.color, background:s.bg, border:`1px solid ${s.border}`,
      padding:"3px 10px", borderRadius:20 }}>
      {tag}
    </span>
  );
}

// ─── Hero Card ─────────────────────────────────────────────────────────────
function HeroCard({ blog, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:BRAND.surface, borderRadius:20, border:`1px solid ${BRAND.border}`,
        overflow:"hidden", cursor:"pointer", marginBottom:"2.5rem",
        display:"grid", gridTemplateColumns:"5fr 7fr",
        boxShadow: hov ? "0 12px 40px rgba(122,0,36,.1)" : "0 2px 12px rgba(0,0,0,.04)",
        transform: hov ? "translateY(-3px)" : "translateY(0)",
        transition:"all .25s ease",
      }}>

      {/* ── Hero thumbnail ── */}
      <div style={{
        background:blog.color||BRAND.redLight, display:"flex",
        alignItems:"center", justifyContent:"center",
        minHeight:300, borderRight:`1px solid ${BRAND.border}`,
        overflow:"hidden"
      }}>
        <BlogThumb blog={blog} style={{ fontSize:"7rem" }} />
      </div>

      <div style={{ padding:"3rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:"1.25rem" }}>
          <TagPill tag={blog.tag} />
          <span style={{ fontSize:"12px", color:BRAND.muted, fontWeight:500 }}>Featured</span>
        </div>
        <h2 style={{
          fontFamily:"'Playfair Display',Georgia,serif",
          fontSize:"clamp(1.5rem,2.5vw,2rem)", fontWeight:700,
          color: hov ? BRAND.red : BRAND.dark,
          lineHeight:1.3, marginBottom:"1rem", transition:"color .2s",
        }}>
          {blog.title}
        </h2>
        <p style={{ fontSize:"1rem", color:BRAND.body, lineHeight:1.8, marginBottom:"1.5rem" }}>
          {blog.excerpt}
        </p>
        <div style={{ display:"flex", alignItems:"center", gap:".85rem",
          fontSize:"13px", color:BRAND.muted, marginBottom:"2rem" }}>
          <span style={{ fontWeight:600, color:BRAND.body }}>
            {blog.author?.trim() ? blog.author : "Yuukke Team"}
          </span>
          <span>·</span><span>{blog.readTime}</span>
          <span>·</span><span>{blog.createdAt}</span>
        </div>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8,
          color:BRAND.red, fontWeight:700, fontSize:"14px",
          borderBottom:`2px solid ${BRAND.redBorder}`, paddingBottom:"2px" }}>
          Read article
          <span style={{ transition:"transform .2s",
            transform: hov ? "translateX(5px)" : "translateX(0)" }}>→</span>
        </div>
      </div>
    </div>
  );
}

// ─── Grid Card ─────────────────────────────────────────────────────────────
function GridCard({ blog, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:BRAND.surface, borderRadius:16, border:`1px solid ${BRAND.border}`,
        overflow:"hidden", cursor:"pointer", display:"flex", flexDirection:"column",
        boxShadow: hov ? "0 8px 28px rgba(122,0,36,.1)" : "0 2px 8px rgba(0,0,0,.04)",
        transform: hov ? "translateY(-4px)" : "translateY(0)",
        transition:"all .22s ease",
      }}>

      {/* ── Grid thumbnail ── */}
      <div style={{
        height:190, background:blog.color||BRAND.redLight,
        display:"flex", alignItems:"center", justifyContent:"center",
        overflow:"hidden", flexShrink:0,
        borderBottom:`1px solid ${blog.borderColor||BRAND.border}`
      }}>
        <BlogThumb blog={blog} style={{ fontSize:"4rem" }} />
      </div>

      <div style={{ padding:"1.5rem", flex:1, display:"flex", flexDirection:"column" }}>
        <div style={{ marginBottom:".85rem" }}><TagPill tag={blog.tag} /></div>
        <h3 style={{
          fontFamily:"'Playfair Display',Georgia,serif",
          fontSize:"1.1rem", fontWeight:700, lineHeight:1.4,
          color: hov ? BRAND.red : BRAND.dark,
          transition:"color .2s", marginBottom:".65rem",
        }}>
          {blog.title}
        </h3>
        <p style={{ fontSize:".875rem", color:BRAND.body, lineHeight:1.7,
          marginBottom:"1rem", flex:1,
          display:"-webkit-box", WebkitLineClamp:3,
          WebkitBoxOrient:"vertical", overflow:"hidden" }}>
          {blog.excerpt}
        </p>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
          paddingTop:".85rem", borderTop:`1px solid ${BRAND.border}`,
          fontSize:"12px", color:BRAND.muted }}>
          <span style={{ fontWeight:600, color:BRAND.body }}>
            {blog.author?.trim() ? blog.author : "Yuukke Team"}
          </span>
          <span>{blog.readTime}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Blog Modal ─────────────────────────────────────────────────────────────
function BlogModal({ blog, onClose }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!blog?.id) return;
    setLoading(true);
    BlogApi.getBlogById(blog.id)
      .then(({ data }) => {
        const b = data?.data && typeof data.data === "object" && !Array.isArray(data.data)
          ? data.data : data;
        setDetail(b);
      })
      .catch(() => setDetail(blog))
      .finally(() => setLoading(false));
  }, [blog?.id]);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = ""; };
  }, [onClose]);

  const b = detail || blog;
  const tagStyle = TAG_STYLE[b?.tag] || { color:BRAND.body, bg:BRAND.bg, border:BRAND.border };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position:"fixed", inset:0, background:"rgba(26,10,15,.55)",
        zIndex:9999, display:"flex", alignItems:"flex-start",
        justifyContent:"center", overflowY:"auto", padding:"2.5rem 1rem" }}>

      <div style={{ background:BRAND.surface, borderRadius:24, width:"100%", maxWidth:760,
        boxShadow:"0 32px 80px rgba(122,0,36,.18)", marginBottom:"2.5rem",
        border:`1px solid ${BRAND.border}`, overflow:"hidden" }}>

        {/* ── Modal header — full-width thumbnail banner if available ── */}
        {b.thumbnailUrl?.trim() ? (
          <div style={{ position:"relative", width:"100%", height:240, overflow:"hidden" }}>
            <BlogThumb blog={b} style={{}} />
            {/* Dark gradient overlay */}
            <div style={{
              position:"absolute", inset:0,
              background:"linear-gradient(to bottom, rgba(0,0,0,.15) 0%, rgba(26,10,15,.7) 100%)"
            }} />
            {/* Text over image */}
            <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"1.5rem 2rem" }}>
              <div style={{ fontSize:"2rem", marginBottom:".4rem" }}>{b.emoji}</div>
              <div style={{ marginBottom:".5rem" }}><TagPill tag={b.tag} /></div>
              <h2 style={{
                fontFamily:"'Playfair Display',Georgia,serif",
                fontSize:"clamp(1.3rem,3vw,1.8rem)", fontWeight:700,
                color:"#fff", lineHeight:1.3, margin:"0 0 .5rem"
              }}>{b.title}</h2>
              <div style={{ display:"flex", gap:"1rem", fontSize:"13px",
                color:"rgba(255,255,255,.8)", flexWrap:"wrap" }}>
                <span style={{ fontWeight:600 }}>{b.author?.trim() ? b.author : "Yuukke Team"}</span>
                <span>·</span><span>{b.readTime}</span>
                <span>·</span><span>{b.createdAt}</span>
              </div>
            </div>
            {/* Close button */}
            <button onClick={onClose} style={{
              position:"absolute", top:"1.25rem", right:"1.25rem",
              background:"rgba(255,255,255,.85)", border:`1px solid ${BRAND.border}`,
              borderRadius:8, padding:".35rem .8rem", cursor:"pointer",
              fontSize:"13px", fontWeight:600, color:BRAND.body,
              fontFamily:"'DM Sans',system-ui,sans-serif",
            }}>✕ Close</button>
          </div>
        ) : (
          /* No thumbnail — original colored header */
          <div style={{ background:b.color||BRAND.redLight, padding:"2.5rem 2.5rem 2rem",
            borderBottom:`1px solid ${b.borderColor||BRAND.border}`, position:"relative" }}>
            <button onClick={onClose} style={{
              position:"absolute", top:"1.25rem", right:"1.25rem",
              background:"rgba(255,255,255,.8)", border:`1px solid ${BRAND.border}`,
              borderRadius:8, padding:".35rem .8rem", cursor:"pointer",
              fontSize:"13px", fontWeight:600, color:BRAND.body,
              fontFamily:"'DM Sans',system-ui,sans-serif",
            }}>✕ Close</button>
            <div style={{ fontSize:"2.5rem", marginBottom:".85rem" }}>{b.emoji}</div>
            <div style={{ marginBottom:".85rem" }}><TagPill tag={b.tag} /></div>
            <h2 style={{ fontFamily:"'Playfair Display',Georgia,serif",
              fontSize:"clamp(1.5rem,3vw,2rem)", fontWeight:700,
              color:BRAND.dark, lineHeight:1.3, margin:"0 0 .85rem" }}>
              {b.title}
            </h2>
            <div style={{ display:"flex", gap:"1rem", fontSize:"13px",
              color:BRAND.muted, flexWrap:"wrap" }}>
              <span style={{ fontWeight:600, color:BRAND.body }}>
                {b.author?.trim() ? b.author : "Yuukke Team"}
              </span>
              <span>·</span><span>{b.readTime}</span>
              <span>·</span><span>{b.createdAt}</span>
            </div>
          </div>
        )}

        {/* ── Modal body ── */}
        <div style={{ padding:"2.5rem" }}>
          {loading ? (
            <div style={{ textAlign:"center", padding:"3rem", color:BRAND.muted }}>
              <div style={{ fontSize:"2rem", marginBottom:".75rem" }}>⏳</div>
              Loading article...
            </div>
          ) : (
            <>
              <p style={{ fontSize:"1.1rem", color:BRAND.body, lineHeight:1.85,
                borderLeft:`3px solid ${BRAND.red}`, paddingLeft:"1.25rem",
                marginBottom:"2rem", fontStyle:"italic",
                fontFamily:"'Playfair Display',Georgia,serif" }}>
                {b.excerpt}
              </p>
              <div className="yk-article-body"
                style={{ fontSize:"1rem", lineHeight:1.85, color:BRAND.dark }}
                dangerouslySetInnerHTML={{ __html: b.content }}
              />
              <style>{`
                .yk-article-body h2{font-family:'Playfair Display',Georgia,serif;font-size:1.3rem;font-weight:700;color:${BRAND.dark};margin:2rem 0 .75rem;line-height:1.35}
                .yk-article-body p{margin-bottom:1.1rem;color:${BRAND.body};line-height:1.85}
                .yk-article-body ul{margin:0 0 1.1rem 1.5rem}
                .yk-article-body li{margin-bottom:.5rem;color:${BRAND.body}}
                .yk-article-body strong{font-weight:700;color:${BRAND.dark}}
              `}</style>
            </>
          )}

          <div style={{ marginTop:"2.5rem", background:BRAND.bg,
            border:`1px solid ${BRAND.border}`, borderRadius:16,
            padding:"2rem", textAlign:"center" }}>
            <p style={{ fontSize:"1.1rem", fontWeight:700, color:BRAND.dark,
              marginBottom:".5rem",
              fontFamily:"'Playfair Display',Georgia,serif" }}>
              Ready to grow your service business?
            </p>
            <p style={{ fontSize:".9rem", color:BRAND.body, marginBottom:"1.5rem" }}>
              Join 500+ women providers on Yuukke — ₹4,999 lifetime deal
            </p>
            <button
              onClick={() => window.location.href = "/yuukke-service#yk2-getstarted"}
              style={{ background:BRAND.red, color:"#fff", border:"none",
                borderRadius:10, padding:".8rem 2.25rem", fontWeight:700,
                fontSize:".95rem", cursor:"pointer",
                fontFamily:"'DM Sans',system-ui,sans-serif" }}>
              Start for free →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────
function BlogListPage({ history }) {
  const [blogs,     setBlogs]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [search,    setSearch]    = useState("");
  const [activeTag, setActiveTag] = useState("All");
  const [selected,  setSelected]  = useState(null);

  useEffect(() => {
    BlogApi.getPublishedBlogs()
      .then((response) => {
        const raw = response?.data;
        let list = [];
        if (Array.isArray(raw?.data)) list = raw.data;
        else if (Array.isArray(raw))  list = raw;
        setBlogs(list);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const filtered = blogs.filter(b => {
    const matchTag    = activeTag === "All" || b.tag === activeTag;
    const matchSearch = !search.trim() ||
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      (b.excerpt||"").toLowerCase().includes(search.toLowerCase());
    return matchTag && matchSearch;
  });

  const showHero = activeTag === "All" && !search.trim() && filtered.length > 0;

  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif",
      background:BRAND.bg, minHeight:"100vh", color:BRAND.dark }}>

      {/* ── NAV ── */}
      <nav style={{ background:BRAND.red, padding:".85rem 2rem",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        position:"sticky", top:0, zIndex:100 }}>
        <a href="/yuukke-service" style={{ textDecoration:"none" }}>
          <img src={headerImg} alt="Yuukke" style={{ height:32 }} />
        </a>
        <div style={{ display:"flex", alignItems:"center", gap:"1.5rem" }}>
          <a href="/yuukke-service#yk2-features"
            style={{ color:"rgba(255,255,255,.85)", textDecoration:"none", fontSize:".95rem", fontWeight:500 }}>
            Features
          </a>
          <a href="/yuukke-service#yk2-pricing"
            style={{ color:"rgba(255,255,255,.85)", textDecoration:"none", fontSize:".95rem", fontWeight:500 }}>
            Pricing
          </a>
          <a href="/yuukke-service#yk2-faq"
            style={{ color:"rgba(255,255,255,.85)", textDecoration:"none", fontSize:".95rem", fontWeight:500 }}>
            FAQ
          </a>
        </div>
        <button
          onClick={() => history ? history.push("/yuukke-service") : window.location.href="/yuukke-service"}
          style={{ background:"#fff", color:BRAND.red, border:"none", borderRadius:8,
            padding:".5rem 1.25rem", fontWeight:700, fontSize:".9rem",
            cursor:"pointer", fontFamily:"'DM Sans',system-ui,sans-serif" }}>
          ← Back to Home
        </button>
      </nav>

      {/* ── HERO HEADER ── */}
      <div style={{ background:BRAND.surface, borderBottom:`1px solid ${BRAND.border}`,
        padding:"5rem 2rem 4rem" }}>
        <div style={{ maxWidth:680, margin:"0 auto", textAlign:"center" }}>
          <div style={{ display:"inline-block", fontSize:"11px", fontWeight:700,
            letterSpacing:".12em", textTransform:"uppercase", color:BRAND.red,
            background:BRAND.redLight, border:`1px solid ${BRAND.redBorder}`,
            borderRadius:20, padding:"5px 16px", marginBottom:"1.25rem" }}>
            Blog & Stories
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',Georgia,serif",
            fontSize:"clamp(2.25rem,5vw,3.25rem)", fontWeight:700,
            color:BRAND.dark, lineHeight:1.2, marginBottom:"1.25rem" }}>
            Learn from Women Who've Been There
          </h1>
          <p style={{ fontSize:"1.1rem", color:BRAND.body, lineHeight:1.75,
            marginBottom:"2.5rem", maxWidth:500, margin:"0 auto 2.5rem" }}>
            Real stories, practical tips, and guides to help you grow your coaching business in India.
          </p>
          {/* Search */}
          <div style={{ position:"relative", maxWidth:500, margin:"0 auto" }}>
            <span style={{ position:"absolute", left:"1.1rem", top:"50%",
              transform:"translateY(-50%)", fontSize:".95rem",
              color:BRAND.muted, pointerEvents:"none" }}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search articles..."
              style={{ width:"100%", padding:".95rem 1rem .95rem 3rem",
                borderRadius:12, border:`1.5px solid ${BRAND.border}`,
                fontSize:"1rem", outline:"none", boxSizing:"border-box",
                fontFamily:"'DM Sans',system-ui,sans-serif", color:BRAND.dark,
                background:BRAND.surface, transition:"border-color .2s" }}
              onFocus={e => e.target.style.borderColor = BRAND.red}
              onBlur={e => e.target.style.borderColor = BRAND.border}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{
                position:"absolute", right:".9rem", top:"50%",
                transform:"translateY(-50%)", background:"none",
                border:"none", cursor:"pointer", color:BRAND.muted, fontSize:"1rem"
              }}>✕</button>
            )}
          </div>
        </div>
      </div>

      {/* ── TAG FILTER BAR ── */}
      <div style={{ background:BRAND.surface, borderBottom:`1px solid ${BRAND.border}`,
        padding:".85rem 2rem", display:"flex", alignItems:"center",
        gap:".5rem", overflowX:"auto" }}>
        {TAGS.map(tag => {
          const s = TAG_STYLE[tag] || {};
          const active = activeTag === tag;
          return (
            <button key={tag} onClick={() => setActiveTag(tag)} style={{
              padding:".45rem 1.1rem", borderRadius:20, cursor:"pointer",
              fontWeight: active ? 700 : 500, fontSize:"14px",
              whiteSpace:"nowrap", transition:"all .15s",
              fontFamily:"'DM Sans',system-ui,sans-serif",
              border: active
                ? `1.5px solid ${tag==="All" ? BRAND.red : s.border}`
                : `1.5px solid ${BRAND.border}`,
              background: active ? (tag==="All" ? BRAND.red : s.bg) : BRAND.surface,
              color:      active ? (tag==="All" ? "#fff"    : s.color) : BRAND.body,
            }}>
              {tag}
            </button>
          );
        })}
        <span style={{ marginLeft:"auto", fontSize:"13px", color:BRAND.muted,
          whiteSpace:"nowrap", paddingLeft:"1rem" }}>
          {filtered.length} article{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth:1200, margin:"0 auto", padding:"3rem 2rem 5rem" }}>
        {loading && <Skeleton />}

        {error && !loading && (
          <div style={{ textAlign:"center", padding:"5rem 2rem", color:BRAND.body }}>
            <div style={{ fontSize:"2.5rem", marginBottom:"1rem" }}>⚠️</div>
            <p style={{ fontSize:"1rem", marginBottom:"1.5rem" }}>Could not load articles right now.</p>
            <button onClick={() => window.location.reload()}
              style={{ background:BRAND.red, color:"#fff", border:"none",
                borderRadius:8, padding:".7rem 1.5rem", fontWeight:700,
                cursor:"pointer", fontFamily:"'DM Sans',system-ui,sans-serif" }}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"5rem 2rem", color:BRAND.body }}>
            <div style={{ fontSize:"3rem", marginBottom:"1rem" }}>📭</div>
            <p style={{ fontSize:"1.1rem", fontWeight:700, color:BRAND.dark, marginBottom:".5rem",
              fontFamily:"'Playfair Display',Georgia,serif" }}>
              {search ? `No results for "${search}"` : "No articles yet"}
            </p>
            <p style={{ fontSize:".9rem", marginBottom:"1.5rem" }}>
              {search ? "Try different keywords or clear the filter"
                      : "Check back soon — new articles every week!"}
            </p>
            {(search || activeTag !== "All") && (
              <button onClick={() => { setSearch(""); setActiveTag("All"); }}
                style={{ background:BRAND.redLight, color:BRAND.red,
                  border:`1px solid ${BRAND.redBorder}`, borderRadius:8,
                  padding:".6rem 1.4rem", fontWeight:700, cursor:"pointer",
                  fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                Clear filters
              </button>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <>
            {showHero && <HeroCard blog={filtered[0]} onClick={() => setSelected(filtered[0])} />}
            <div style={{ display:"grid",
              gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:"1.5rem" }}>
              {(showHero ? filtered.slice(1) : filtered).map(blog => (
                <GridCard key={blog.id} blog={blog} onClick={() => setSelected(blog)} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── FOOTER CTA ── */}
      <div style={{ background:BRAND.red, padding:"4rem 2rem", textAlign:"center" }}>
        <p style={{ fontSize:"11px", fontWeight:700, letterSpacing:".12em",
          textTransform:"uppercase", color:"rgba(255,255,255,.6)", marginBottom:"1rem" }}>
          Get Started
        </p>
        <h2 style={{ fontFamily:"'Playfair Display',Georgia,serif",
          fontSize:"clamp(1.5rem,3vw,2rem)", fontWeight:700,
          color:"#fff", marginBottom:"1rem", lineHeight:1.3 }}>
          Start getting paid for your sessions
        </h2>
        <p style={{ color:"rgba(255,255,255,.75)", marginBottom:"2rem", fontSize:".95rem" }}>
          Join 500+ women service providers on Yuukke — ₹4,999 one-time deal
        </p>
        <a href="/yuukke-service#yk2-getstarted"
          style={{ display:"inline-block", background:"#fff", color:BRAND.red,
            borderRadius:10, padding:".85rem 2.5rem", fontWeight:700,
            fontSize:"1rem", textDecoration:"none" }}>
          Get Started →
        </a>
      </div>

      {selected && <BlogModal blog={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default withTranslation()(withRouter(BlogListPage));